import { useChatHistoryQuery } from "@src/features/assistantChat/aiChat.generated";
import type { AiChatFragment } from "@src/features/assistantChat/aiChat.generated";
import PaginatedList from "@src/features/library/PaginatedList";
import Text from "@src/lib/ui/Text";
import { AiChatType, MyAiChatsInput } from "@src/types.generated";
import { useMemo, useRef } from "react";
import { getFormattedDistanceToNow } from "@src/features/libraryItem/course/utils/utils";
import ScrollableFlexGrow from "@src/lib/ui/ScrollableFlexGrow";

export type ChatHistoryProps = {
  filters: {
    chatType: AiChatType;
  };
  onClickPreviousChat: (chat: AiChatFragment) => void;
};

export function ChatHistory({
  filters,
  onClickPreviousChat,
}: ChatHistoryProps) {
  const scrollableRef = useRef<HTMLDivElement>(null);
  const input: MyAiChatsInput = useMemo(() => {
    return {
      type: filters.chatType,
    };
  }, [filters]);
  return (
    <div>
      <div className="px-2.5 py-2 border-b border-gray-2">
        <Text variant="compact-title">Chat history</Text>
      </div>
      <ScrollableFlexGrow className="h-[320px]" innerRef={scrollableRef}>
        <PaginatedList<AiChatFragment, typeof useChatHistoryQuery>
          queryHook={useChatHistoryQuery}
          getTotalItemCount={(data) => data?.MyAiChats?.totalCount ?? 0}
          getItems={(data) => data?.MyAiChats?.objects ?? []}
          itemId={(item) => item.id}
          input={input}
          className="flex flex-col gap-0.5 p-1"
          viewportRef={scrollableRef}
          renderItem={(item) => (
            <div
              className="gap-1 cursor-pointer flex justify-between items-center px-1.5 py-1 rounded-md transition-colors duration-200 bg-white hover:bg-gray-1"
              onClick={() => onClickPreviousChat(item)}
            >
              <Text
                variant="fine-print-description"
                ellipsis
                style={{
                  flex: 1,
                  alignSelf: "stretch",
                }}
                className="text-left"
              >
                {item.title}
              </Text>
              <Text
                variant="fine-print-description"
                className="text-right text-muted-foreground"
              >
                {getFormattedDistanceToNow(new Date(item.createdAt))}
              </Text>
            </div>
          )}
          noDataEmptyState={
            <Text variant="fine-print-description" color="muted">
              No chat history
            </Text>
          }
          dataTestId="chat-history-list"
        />
      </ScrollableFlexGrow>
    </div>
  );
}
